/**
 * HotelBookingSuccessPage — landing after /api/hotels/book succeeds.
 */
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, Calendar, MapPin, Mail, Home, Hotel as HotelIcon, Clock, XCircle, AlertTriangle } from 'lucide-react';
import Footer from '../components/Footer';
import BottomNav from '../components/BottomNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HotelBookingSuccessPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const bookingId = params.get('bookingId');

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionBusy, setActionBusy] = useState(false);
  const [actionMsg, setActionMsg] = useState('');

  const reload = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/hotels/booking/${encodeURIComponent(bookingId)}`, { timeout: 30000 });
      if (res.data?.success) setBooking(res.data.booking);
      else setError('Could not load booking.');
    } catch (e) {
      setError(e.response?.data?.detail?.message || 'Network error.');
    }
  };

  useEffect(() => {
    if (!bookingId) {
      setError('Booking reference missing.');
      setLoading(false);
      return;
    }
    (async () => { await reload(); setLoading(false); })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  const handleConfirmHold = async () => {
    setActionBusy(true); setActionMsg('');
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/hotels/confirm-hold/${encodeURIComponent(bookingId)}`,
        { payment_mode: booking?.payment_mode === 'credit' ? 'credit' : 'wallet' },
        { timeout: 60000 }
      );
      if (res.data?.success) {
        setActionMsg('Hold confirmed. Booking is now CONFIRMED.');
        await reload();
      } else {
        setActionMsg('Confirm-hold failed.');
      }
    } catch (e) {
      setActionMsg(e.response?.data?.detail?.message || 'Confirm-hold failed.');
    } finally {
      setActionBusy(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this booking? This action cannot be undone.')) return;
    setActionBusy(true); setActionMsg('');
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/hotels/cancel/${encodeURIComponent(bookingId)}`,
        { reason: 'user_initiated' },
        { timeout: 60000 }
      );
      if (res.data?.success) {
        setActionMsg(`Cancellation status: ${res.data.status}. ${res.data.refunded ? 'Refund processed.' : ''}`);
        await reload();
      } else {
        setActionMsg('Cancellation failed.');
      }
    } catch (e) {
      setActionMsg(e.response?.data?.detail?.message || 'Cancellation failed.');
    } finally {
      setActionBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#1B375B]">
        <Loader2 className="w-8 h-8 animate-spin mr-2" /> Confirming…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F7F8] pb-24 md:pb-12" data-testid="hotel-success-page">
      <div className="bg-gradient-to-b from-[#1B375B] to-[#659A9B] text-white py-10 sm:py-14">
        <div className="max-w-4xl mx-auto px-4 text-center">
          {error ? (
            <>
              <div className="w-14 h-14 mx-auto rounded-full bg-red-500/20 flex items-center justify-center mb-4">
                <HotelIcon className="w-7 h-7 text-red-200" />
              </div>
              <h1 className="text-xl sm:text-2xl font-extrabold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{error}</h1>
            </>
          ) : (
            <>
              <div className={`w-14 h-14 mx-auto rounded-full ${booking?.status === 'ON_HOLD' ? 'bg-amber-500/20' : booking?.status === 'CANCELLED' ? 'bg-red-500/20' : 'bg-emerald-500/20'} flex items-center justify-center mb-4`}>
                {booking?.status === 'ON_HOLD' ? (
                  <Clock className="w-9 h-9 text-amber-200" data-testid="hotel-success-clock" />
                ) : booking?.status === 'CANCELLED' || booking?.status === 'CANCELLATION_PENDING' ? (
                  <XCircle className="w-9 h-9 text-red-200" data-testid="hotel-success-cancelled-icon" />
                ) : (
                  <CheckCircle2 className="w-9 h-9 text-emerald-300" data-testid="hotel-success-check" />
                )}
              </div>
              <h1 className="text-2xl sm:text-3xl font-extrabold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {booking?.status === 'ON_HOLD' ? 'Booking on Hold' : booking?.status === 'CANCELLED' ? 'Booking Cancelled' : booking?.status === 'CANCELLATION_PENDING' ? 'Cancellation Pending' : 'Booking Confirmed!'}
              </h1>
              <p className="text-sm text-white/80">
                {booking?.status === 'ON_HOLD' ? 'The room is reserved. Confirm before the deadline to ticket it.'
                 : booking?.status === 'CANCELLED' ? 'Your booking has been cancelled.'
                 : booking?.status === 'CANCELLATION_PENDING' ? 'Cancellation is being processed by the supplier.'
                 : 'Your hotel reservation has been successfully booked.'}
              </p>
            </>
          )}
        </div>
      </div>

      {booking && (
        <div className="max-w-4xl mx-auto px-3 sm:px-6 py-6 -mt-6">
          <div className="bg-white rounded-2xl shadow-xl p-5 sm:p-8 space-y-5">
            <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-gray-200">
              <div>
                <p className="text-[10px] text-gray-400 uppercase font-bold tracking-wider">Booking Reference</p>
                <p className="text-base sm:text-lg font-extrabold text-[#1B375B]" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="hotel-success-booking-id">
                  {booking.booking_id}
                </p>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-bold ${booking.status === 'CONFIRMED' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`} data-testid="hotel-success-status">
                {booking.status}
              </span>
            </div>

            <div>
              <h3 className="text-sm font-bold text-[#1B375B] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Stay Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-gray-700">
                {booking.hotel_meta?.hotelName && (
                  <div className="flex items-start gap-2">
                    <HotelIcon className="w-4 h-4 text-[#659A9B] mt-0.5" />
                    <div><span className="font-semibold">{booking.hotel_meta.hotelName}</span></div>
                  </div>
                )}
                {booking.hotel_meta?.city && (
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-[#659A9B] mt-0.5" />
                    <div>{booking.hotel_meta.city}</div>
                  </div>
                )}
                {(booking.hotel_meta?.checkin && booking.hotel_meta?.checkout) && (
                  <div className="flex items-start gap-2 sm:col-span-2">
                    <Calendar className="w-4 h-4 text-[#659A9B] mt-0.5" />
                    <div>{booking.hotel_meta.checkin} → {booking.hotel_meta.checkout}</div>
                  </div>
                )}
                <div className="flex items-start gap-2">
                  <Mail className="w-4 h-4 text-[#659A9B] mt-0.5" />
                  <div>{booking.delivery?.emails?.[0]}</div>
                </div>
              </div>

              {/* Phase H6.C — Per-room summary for multi-room bookings. */}
              {Array.isArray(booking.hotel_meta?.rooms_in_option) && booking.hotel_meta.rooms_in_option.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-100" data-testid="hotel-success-rooms-summary">
                  <h4 className="text-xs font-bold text-[#659A9B] uppercase tracking-wider mb-2">
                    {booking.hotel_meta.rooms_in_option.length} room{booking.hotel_meta.rooms_in_option.length !== 1 ? 's' : ''} booked
                  </h4>
                  <div className="space-y-2">
                    {booking.hotel_meta.rooms_in_option.map((ri, riIdx) => {
                      const _rType = ri.rt || ri.rc || `Room ${riIdx + 1}`;
                      const _adt = ri.adt || 0;
                      const _chd = ri.chd || 0;
                      const _mb = ri.mb;
                      const _bt = ri.bt || ri.bedType;
                      return (
                        <div key={riIdx} className="flex items-start gap-2 text-sm text-gray-700" data-testid={`hotel-success-room-${riIdx}`}>
                          <span className="inline-flex items-center justify-center w-5 h-5 rounded text-[10px] font-extrabold bg-[#1B375B] text-white shrink-0 mt-0.5">{riIdx + 1}</span>
                          <div className="min-w-0">
                            <p className="font-bold text-[#1B375B] leading-tight">{_rType}</p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {_bt && <>{_bt} · </>}{_adt} adult{_adt !== 1 ? 's' : ''}{_chd ? `, ${_chd} child${_chd !== 1 ? 'ren' : ''}` : ''}
                              {_mb && <span className="text-amber-700"> · {Array.isArray(_mb) ? _mb.join(', ') : _mb}</span>}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-sm font-bold text-[#1B375B] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Payment Summary</h3>
              <div className="space-y-1 text-sm text-gray-700">
                <div className="flex justify-between"><span>Room amount</span><span>₹{Number(booking.base_amount || 0).toLocaleString('en-IN')}</span></div>
                {booking.markup_amount > 0 && (
                  <div className="flex justify-between text-xs text-gray-500"><span>Service fee</span><span>₹{Number(booking.markup_amount).toLocaleString('en-IN')}</span></div>
                )}
                <div className="flex justify-between font-bold text-[#1B375B] pt-1 border-t border-gray-200">
                  <span>Total Paid</span>
                  <span className="text-base" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="hotel-success-total">
                    ₹{Number(booking.total_amount || 0).toLocaleString('en-IN')}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 pt-3">
              <Link to="/" className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#1B375B] text-white rounded-lg text-xs font-bold hover:bg-[#659A9B]" data-testid="hotel-success-home">
                <Home className="w-3.5 h-3.5" /> Home
              </Link>
              <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 px-4 py-2 bg-gray-100 text-[#1B375B] rounded-lg text-xs font-bold hover:bg-gray-200">
                Print Receipt
              </button>
              {booking.status === 'ON_HOLD' && (
                <button
                  onClick={handleConfirmHold}
                  disabled={actionBusy}
                  data-testid="hotel-success-confirm-hold"
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white rounded-lg text-xs font-bold hover:bg-emerald-700 disabled:opacity-60"
                >
                  {actionBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Confirm & Pay
                </button>
              )}
              {(booking.status === 'CONFIRMED' || booking.status === 'ON_HOLD' || booking.status === 'PENDING') && (
                <button
                  onClick={handleCancel}
                  disabled={actionBusy}
                  data-testid="hotel-success-cancel"
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-white text-red-600 border border-red-300 rounded-lg text-xs font-bold hover:bg-red-50 disabled:opacity-60"
                >
                  {actionBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />} Cancel Booking
                </button>
              )}
            </div>

            {booking.status === 'ON_HOLD' && booking.hold_until && (
              <div className="bg-amber-50 border border-amber-300 rounded-xl p-3 flex items-start gap-2 mt-2" data-testid="hotel-success-hold-banner">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <div className="flex-1 text-xs text-amber-800">
                  <p className="font-bold">Confirm before the deadline</p>
                  <p>Pay ₹{Number(booking.total_amount || 0).toLocaleString('en-IN')} before <strong>{String(booking.hold_until).slice(0, 19).replace('T', ' ')}</strong> or the hold will be released.</p>
                </div>
              </div>
            )}

            {actionMsg && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-800" data-testid="hotel-success-action-msg">{actionMsg}</div>
            )}
          </div>
        </div>
      )}

      <Footer />
      <BottomNav />
    </div>
  );
};

export default HotelBookingSuccessPage;
